import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Textarea } from '@/components/ui-kit/textarea';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/state/store/auth';
import { useToast } from '@/hooks/use-toast';
import { CompanyPicker } from '@/modules/org/companies/components/CompanyPicker';
import { CompanyForm } from '@/modules/org/companies/components/company-form';
import { CompanySummary } from '@/modules/org/companies/components/company-summary';
import { useCompanyLookup } from '@/modules/org/companies/hooks/use-companies';
import type { Contact, ContactDraft } from '../types/contacts.types';
import type { ContactItem } from '@/graphql/queries/contacts/use-get-contacts';
import { useInsertContacts, useUpdateContact } from '@/graphql';

/** Basic, permissive validators — fields are optional, so only non-empty values are checked. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s().-]{6,}$/;

interface FieldDef {
  key: 'name' | 'email' | 'role' | 'company' | 'phone' | 'address';
  labelKey: string;
  hintKey: string;
  placeholderKey: string;
  required?: boolean;
  type?: string;
  /** Grid columns spanned (1 or 2). */
  span?: 1 | 2;
  multiline?: boolean;
  /** Rendered as a company picker instead of a text input. */
  picker?: boolean;
}

const FIELD_DEFS: FieldDef[] = [
  {
    key: 'name',
    labelKey: 'ORGCONTACTS_FIELD_NAME',
    hintKey: 'ORGCONTACTS_HINT_NAME',
    placeholderKey: 'ORGCONTACTS_PH_NAME',
    required: true,
    span: 2,
  },
  {
    key: 'email',
    labelKey: 'ORGCONTACTS_FIELD_EMAIL',
    hintKey: 'ORGCONTACTS_HINT_EMAIL',
    placeholderKey: 'ORGCONTACTS_PH_EMAIL',
    type: 'email',
    required: true,
    span: 1,
  },
  {
    key: 'phone',
    labelKey: 'ORGCONTACTS_FIELD_PHONE',
    hintKey: 'ORGCONTACTS_HINT_PHONE',
    placeholderKey: 'ORGCONTACTS_PH_PHONE',
    type: 'tel',
    required: true,
    span: 1,
  },
  {
    key: 'role',
    labelKey: 'ORGCONTACTS_FIELD_ROLE',
    hintKey: 'ORGCONTACTS_HINT_ROLE',
    placeholderKey: 'ORGCONTACTS_PH_ROLE',
    span: 2,
  },
  {
    key: 'company',
    labelKey: 'ORGCONTACTS_FIELD_COMPANY',
    hintKey: 'ORGCONTACTS_HINT_COMPANY',
    placeholderKey: 'ORGCONTACTS_PH_COMPANY',
    span: 2,
    picker: true,
  },
  {
    key: 'address',
    labelKey: 'ORGCONTACTS_FIELD_ADDRESS',
    hintKey: 'ORGCONTACTS_HINT_ADDRESS',
    placeholderKey: 'ORGCONTACTS_PH_ADDRESS',
    span: 2,
    multiline: true,
  },
];

type FieldKey = FieldDef['key'];
/** `companyId` shadows `company`: it's set by the picker, never typed directly. */
type FormState = Record<FieldKey, string> & { companyId: string };

const emptyForm: FormState = {
  name: '',
  email: '',
  role: '',
  company: '',
  companyId: '',
  phone: '',
  address: '',
};

interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided the dialog edits this contact; otherwise it creates a new one. */
  contact?: ContactItem | null;
  /** Seed the "share with org" checkbox for new contacts (e.g. from the org tab). */
  defaultShareWithOrg?: boolean;
  /** Prefill values for create mode (e.g. name typed in the contact picker). */
  defaults?: ContactDraft;
  /** Hide the "share with org" checkbox (forces a personal contact). */
  canShareWithOrg?: boolean;
  /** Fired after a successful create with the new contact — used by the picker to auto-select it. */
  onCreated?: (contact: Contact) => void;
  orgName?: string;
}

export function ContactFormDialog({
  open,
  onOpenChange,
  contact,
  defaultShareWithOrg = false,
  defaults,
  canShareWithOrg = true,
  onCreated,
  orgName,
}: ContactFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const orgUDSId = useAuthStore((s) => s.orgUDSId);
  const { insertContactsAsync, isPending: isCreating } = useInsertContacts();
  const { updateContactAsync, isPending: isUpdating } = useUpdateContact();

  const isEdit = !!contact;
  const isBusy = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [shareWithOrg, setShareWithOrg] = useState(defaultShareWithOrg);
  // Which form this dialog is currently showing. Switching does NOT touch `form`, so the
  // contact's entries survive a detour into creating a company.
  const [view, setView] = useState<'contact' | 'company'>('contact');
  const [companyPrefill, setCompanyPrefill] = useState('');

  // Reseed whenever the dialog opens (or the target contact changes) so create
  // and edit modes always start from the right values.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setView('contact');
    if (contact) {
      setForm({
        name: contact.Name ?? '',
        email: contact.Email ?? '',
        role: contact.Role ?? '',
        company: contact.Company ?? '',
        companyId: contact.CompanyId ?? '',
        phone: contact.Phone ?? '',
        address: contact.Address ?? '',
      });
    } else {
      setForm({
        name: defaults?.name ?? '',
        email: defaults?.email ?? '',
        role: defaults?.role ?? '',
        company: defaults?.company ?? '',
        companyId: defaults?.companyId ?? '',
        phone: defaults?.phone ?? '',
        address: defaults?.address ?? '',
      });
      setShareWithOrg(defaultShareWithOrg);
    }
    // `defaults` is only read to seed on open; excluded to avoid reseeding on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact, defaultShareWithOrg]);

  const set = (key: FieldKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  /** Company is chosen from the picker, so name and id always move together. */
  const setCompany = (name: string, companyId: string) =>
    setForm((prev) => ({ ...prev, company: name, companyId }));

  /**
   * Keep the dialog open on an outside click, WITHOUT the ui-kit default's side effect.
   * That default reaches into `detail.originalEvent` and calls preventDefault +
   * stopPropagation on the raw DOM event, which suppresses handlers belonging to layers
   * portaled outside the dialog — i.e. this dialog's own company popover. Cancelling the
   * Radix event alone is enough to block dismissal and leaves other layers working.
   */
  const cancelDismiss = (e: Event) => e.preventDefault();

  const openCompanyView = (prefillName: string) => {
    setCompanyPrefill(prefillName);
    setView('company');
  };
  const backToContact = () => setView('contact');

  // A linked company may have been renamed since this contact was saved, so the
  // live record wins over the stored snapshot for both display and the next save.
  const { byId: companyById } = useCompanyLookup(open && !!form.companyId);
  const linkedCompany = form.companyId ? companyById.get(form.companyId) : undefined;
  const companyName = linkedCompany?.name || form.company.trim();

  const validate = (): boolean => {
    const next: Partial<Record<FieldKey, string>> = {};
    if (!form.name.trim()) next.name = t('ORGCONTACTS_NAME_REQUIRED');
    const email = form.email.trim();
    if (!email) next.email = t('ORGCONTACTS_EMAIL_REQUIRED');
    else if (!EMAIL_RE.test(email)) next.email = t('ORGCONTACTS_INVALID_EMAIL');
    const phone = form.phone.trim();
    if (!phone) next.phone = t('ORGCONTACTS_PHONE_REQUIRED');
    else if (!PHONE_RE.test(phone)) next.phone = t('ORGCONTACTS_INVALID_PHONE');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    const fields = {
      Name: form.name.trim(),
      Email: form.email.trim() || undefined,
      Role: form.role.trim() || undefined,
      Company: companyName || undefined,
      CompanyId: form.companyId.trim() || undefined,
      Phone: form.phone.trim() || undefined,
      Address: form.address.trim() || undefined,
    };

    try {
      if (isEdit && contact) {
        await updateContactAsync({
          ItemId: contact.ItemId,
          ...fields,
          Company: companyName,
          CompanyId: form.companyId.trim(),
        });
      } else {
        if (shareWithOrg && !orgUDSId) {
          toast({ variant: 'destructive', title: t('ORGCONTACTS_SHARE_NO_ORG') });
          return;
        }
        const scope = shareWithOrg ? 'organization' : 'user';
        const result = await insertContactsAsync({
          Scope: scope,
          ...fields,
          ...(scope === 'organization' && orgUDSId ? { OrganizationIds: [orgUDSId] } : {}),
        });
        const id = result.insertContacts?.itemId ?? '';
        toast({ variant: 'success', title: t('ORGCONTACTS_CREATED') });
        onCreated?.({
          id,
          scope,
          name: fields.Name,
          email: form.email.trim(),
          role: form.role.trim(),
          company: companyName,
          companyId: form.companyId.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          organizationId: scope === 'organization' ? (orgUDSId ?? undefined) : undefined,
          canManage: true,
        });
      }
      onOpenChange(false);
    } catch {
      /* mutation hooks surface the error toast */
    }
  };

  // The company form takes over this same dialog instead of stacking a second one. The
  // contact form's state lives in this component, so swapping views preserves every entry.
  if (view === 'company') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          onInteractOutside={cancelDismiss}
          className="max-w-sm md:max-w-xl font-nunito max-h-[85vh] flex flex-col"
        >
          <DialogHeader className="shrink-0">
            <button
              type="button"
              onClick={backToContact}
              className="mb-1 -ml-1 inline-flex w-fit items-center gap-1.5 rounded-md px-1 py-0.5 text-xs font-medium text-globe-grey hover:text-selise-blue hover:bg-muted transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('ORGCONTACTS_BACK_TO_CONTACT')}
            </button>
            <DialogTitle>{t('ORGCOMPANIES_NEW_TITLE')}</DialogTitle>
            <DialogDescription>{t('ORGCOMPANIES_NEW_SUBTITLE')}</DialogDescription>
          </DialogHeader>

          <CompanyForm
            defaults={{ name: companyPrefill }}
            canShareWithOrg
            orgName={orgName}
            onCreated={(c) => setCompany(c.name, c.id)}
            onDone={backToContact}
            bodyClassName="flex-1 min-h-0 overflow-y-auto pr-1"
            footerClassName="shrink-0"
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped to the viewport with a scrolling body — the form is taller than a phone screen. */}
      <DialogContent
        onInteractOutside={cancelDismiss}
        className="max-w-sm md:max-w-xl font-nunito max-h-[85vh] flex flex-col"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isEdit ? t('ORGCONTACTS_EDIT_TITLE') : t('ORGCONTACTS_NEW_TITLE')}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('ORGCONTACTS_EDIT_SUBTITLE') : t('ORGCONTACTS_NEW_SUBTITLE')}
          </DialogDescription>
        </DialogHeader>
        {/* Header and footer stay pinned; only this pane scrolls. `min-h-0` lets it shrink. */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            {FIELD_DEFS.map((field) => {
              const error = errors[field.key];
              const describedBy = error
                ? `contact-${field.key}-error`
                : `contact-${field.key}-hint`;
              return (
                <div
                  key={field.key}
                  className={cn('space-y-1', field.span === 2 && 'sm:col-span-2')}
                >
                  <Label htmlFor={`contact-${field.key}`} className="text-xs font-medium">
                    {t(field.labelKey)}
                    {field.required && <span className="text-crimson"> *</span>}
                  </Label>

                  {field.picker ? (
                    <>
                      <div className="flex items-center gap-2">
                        <CompanyPicker
                          triggerId={`contact-${field.key}`}
                          value={companyName}
                          placeholder={t('ORGCONTACTS_PICK_COMPANY')}
                          triggerClassName="h-9"
                          onSelect={(c) => setCompany(c.name, c.id)}
                          onClear={() => setCompany('', '')}
                          onRequestCreate={openCompanyView}
                        />
                      </div>
                      {/* Details live on the company record, never copied onto the contact. */}
                      {linkedCompany && (
                        <CompanySummary
                          company={linkedCompany}
                          onClear={() => setCompany('', '')}
                          clearLabel={t('ORGCONTACTS_CLEAR_COMPANY')}
                        />
                      )}
                    </>
                  ) : field.multiline ? (
                    <Textarea
                      id={`contact-${field.key}`}
                      rows={3}
                      value={form[field.key]}
                      placeholder={t(field.placeholderKey)}
                      onChange={(e) => set(field.key, e.target.value)}
                      aria-invalid={!!error}
                      aria-describedby={describedBy}
                      className="resize-none"
                    />
                  ) : (
                    <Input
                      id={`contact-${field.key}`}
                      type={field.type ?? 'text'}
                      value={form[field.key]}
                      placeholder={t(field.placeholderKey)}
                      onChange={(e) => set(field.key, e.target.value)}
                      aria-invalid={!!error}
                      aria-describedby={describedBy}
                      className={cn('h-9', error && 'border-crimson focus-visible:ring-crimson')}
                    />
                  )}

                  {error ? (
                    <p id={`contact-${field.key}-error`} className="text-xs text-crimson">
                      {error}
                    </p>
                  ) : (
                    <p id={`contact-${field.key}-hint`} className="text-xs text-globe-grey">
                      {t(field.hintKey)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {isEdit ? (
            <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-globe-grey mt-2">
              {contact?.Scope === 'organization' ? (
                <Trans
                  i18nKey="ORGCONTACTS_SCOPE_NOTE_ORG"
                  values={{ org: orgName || t('ORGCONTACTS_YOUR_ORG') }}
                  components={{ b: <span className="font-semibold text-selise-blue" /> }}
                />
              ) : (
                t('ORGCONTACTS_SCOPE_NOTE_PERSONAL')
              )}
            </p>
          ) : canShareWithOrg ? (
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
              <Checkbox
                checked={shareWithOrg}
                onCheckedChange={(v) => setShareWithOrg(v === true)}
              />
              <span>
                <Trans
                  i18nKey="ORGCONTACTS_SHARE_LABEL"
                  values={{ org: orgName || t('ORGCONTACTS_YOUR_ORG') }}
                  components={{ b: <span className="font-semibold text-selise-blue" /> }}
                />
              </span>
            </label>
          ) : null}
        </div>

        <DialogFooter className="flex flex-row justify-end gap-4 shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isBusy}>
            {t('CANCEL')}
          </Button>
          <Button variant="primary" onClick={submit} disabled={isBusy}>
            {isBusy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {isEdit ? t('ORGCONTACTS_SAVE_CTA') : t('ORGCONTACTS_CREATE_CTA')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ContactFormDialog;
